import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type EquityPoint } from '../api/client';
import { PageHeader } from '../components/Layout';
import { Badge, Card, EmptyBox, KeyValue, QueryBoundary, Spinner, Stat } from '../components/ui';
import { useAccounts } from '../hooks/accounts';
import { ago, dateTime, money, pct, signedMoney } from '../lib/format';

function Sparkline({ points }: { points: EquityPoint[] }) {
  if (points.length < 2) return <EmptyBox>Not enough equity snapshots to draw a curve yet.</EmptyBox>;
  const values = points.map((p) => Number(p.equity));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const w = 640;
  const h = 140;
  const d = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - ((v - min) / span) * h;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const last = values[values.length - 1];
  const first = values[0];
  const up = last >= first;
  return (
    <div className="space-y-2">
      <svg viewBox={`0 0 ${w} ${h}`} className="h-36 w-full" preserveAspectRatio="none">
        <path d={d} fill="none" stroke={up ? '#34d399' : '#f87171'} strokeWidth="1.5" />
      </svg>
      <div className="flex justify-between text-xs text-slate-500">
        <span>{dateTime(points[0].time_utc)}</span>
        <span>
          {money(first)} → {money(last)}
        </span>
        <span>{dateTime(points[points.length - 1].time_utc)}</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { selectedId, selected, isLoading: accountsLoading } = useAccounts();
  const q = useQuery({
    queryKey: ['dashboard', selectedId],
    queryFn: () => api.dashboard(selectedId as number),
    enabled: selectedId != null,
    refetchInterval: 20_000,
  });

  if (accountsLoading) return <Spinner />;
  if (selectedId == null)
    return (
      <>
        <PageHeader title="Dashboard" />
        <EmptyBox>
          No accounts configured. Seed an <code>accounts</code> row (see{' '}
          <Link className="text-sky-400" to="/operations">
            Operations
          </Link>
          ).
        </EmptyBox>
      </>
    );

  const d = q.data;
  const risk = d?.risk;
  const dailyLossLimit =
    risk && risk.peak_equity
      ? -(Number(risk.peak_equity) * Number(risk.max_daily_loss_fraction))
      : null;

  return (
    <>
      <PageHeader
        title="Dashboard"
        description={
          d ? (
            <>
              {d.account_name} · login {d.login ?? '—'} · as of {ago(d.as_of_utc)}
            </>
          ) : (
            selected?.name
          )
        }
        actions={
          risk?.trading_halted ? <Badge tone="bad">HALTED</Badge> : <Badge tone="good">running</Badge>
        }
      />

      <QueryBoundary q={q}>
        {d && risk && (
          <div className="space-y-6">
            {risk.trading_halted && (
              <Card className="border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-200">
                Trading is halted: {risk.halt_reason ?? 'no reason recorded'}. Clear it from{' '}
                <Link className="underline" to="/operations">
                  Operations
                </Link>
                .
              </Card>
            )}

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Equity" value={money(d.equity, d.currency)} />
              <Stat label="Balance" value={money(d.balance, d.currency)} />
              <Stat
                label="Today P&L"
                value={signedMoney(d.today_realized_pnl, d.currency)}
                tone={Number(d.today_realized_pnl) < 0 ? 'bad' : Number(d.today_realized_pnl) > 0 ? 'good' : undefined}
                sub={dailyLossLimit != null ? `loss limit ${money(dailyLossLimit)}` : undefined}
              />
              <Stat
                label="Open positions"
                value={`${d.open_position_count} / ${risk.max_open_positions}`}
                tone={d.open_position_count >= risk.max_open_positions ? 'warn' : undefined}
              />
            </div>

            <Card className="p-4">
              <div className="mb-3 text-sm font-medium text-slate-300">Equity — last 30 days</div>
              <Sparkline points={d.equity_curve} />
            </Card>

            <Card className="p-4">
              <div className="mb-3 text-sm font-medium text-slate-300">Risk limits</div>
              <KeyValue
                rows={[
                  ['Trading mode', d.trading_mode],
                  ['Daily realised P&L', signedMoney(risk.daily_realized_pnl, d.currency)],
                  ['Max daily loss', pct(risk.max_daily_loss_fraction)],
                  ['Max drawdown', pct(risk.max_drawdown_fraction)],
                  ['Peak equity', money(risk.peak_equity, d.currency)],
                  [
                    'Open / max positions',
                    `${risk.open_positions} / ${risk.max_open_positions}`,
                  ],
                  [
                    'Emergency stop',
                    risk.trading_halted ? (
                      <span className="text-red-400">engaged — {risk.halt_reason}</span>
                    ) : (
                      <span className="text-emerald-400">clear</span>
                    ),
                  ],
                ]}
              />
            </Card>

            <div className="flex gap-3 text-sm">
              <Link className="text-sky-400 hover:underline" to="/positions">
                View positions →
              </Link>
              <Link className="text-sky-400 hover:underline" to="/signals">
                Signal funnel →
              </Link>
              <Link className="text-sky-400 hover:underline" to="/events">
                Incidents →
              </Link>
            </div>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}
